<template>
  <v-card max-width="500" width="100%">
    <v-card-title class="text-h5 pt-4 px-4">
      {{ isReconfigure ? 'Configurer le Wi-Fi' : 'Nouveau bouton' }}
    </v-card-title>

    <v-card-text class="px-4">

      <div v-if="step === 'init'" class="d-flex flex-column align-center py-6">
        <v-icon
          class="mb-4"
          color="primary"
          icon="mdi-bluetooth-connect"
          size="64"
        />

        <p class="text-body-1 text-center mb-4">
          Nous allons nous connecter à votre bouton pour détecter les réseaux Wi-Fi environnants.
        </p>

        <p class="text-caption text-center mb-1">
          Cliquez ici pour s'associer au bouton:
        </p>

        <v-btn
          color="primary"
          prepend-icon="mdi-magnify"
          variant="flat"
          @click="connectAndScan"
        >
          Se connecter au bouton
        </v-btn>

        <p class="text-caption text-center mt-1">
          Puis sélectionnez votre bouton dans la fenêtre du navigateur qui s'ouvrira.
        </p>
      </div>

      <div v-else-if="step === 'scanning'" class="d-flex flex-column align-center py-6">
        <v-progress-circular
          class="mb-4"
          color="primary"
          indeterminate
          size="64"
        />

        <p class="text-body-1 font-weight-medium">
          Recherche des réseaux Wi-Fi...
        </p>

        <p class="text-caption text-center mt-2">
          Merci de patienter, cela peut prendre jusqu'à 30 secondes.
        </p>
      </div>

      <div v-else-if="step === 'form'">
        <p class="mb-4 text-body-2 text-grey-darken-1">
          Sélectionnez votre réseau Wi-Fi ou saisissez son nom manuellement.
        </p>

        <v-combobox
          v-model="ssid"
          autocomplete="off"
          class="mb-2"
          density="comfortable"
          :items="availableNetworks"
          label="Nom du réseau Wi-Fi (SSID)"
          name="wifi-ssid-nofill"
          placeholder="ex: Livebox-1234"
          variant="outlined"
        />

        <v-text-field
          v-model="pass"
          autocomplete="off"
          density="comfortable"
          label="Mot de passe Wi-Fi"
          name="wifi-pass-nofill"
          placeholder="ex: monMotDePasse123"
          type="text"
          variant="outlined"
          @keyup.enter="sendCredentialsAndSave"
        />

        <v-select
          v-if="isAdmin"
          v-model="selectedEnv"
          class="mt-4"
          density="comfortable"
          hide-details
          :items="[
            { title: 'Production', value: 'prod' },
            { title: 'Preproduction', value: 'prep' },
            { title: 'Development', value: 'dev' }
          ]"
          label="Environnement cible (Admin)"
          prepend-inner-icon="mdi-server-network"
          variant="outlined"
        />
      </div>

      <div v-else-if="step === 'pairing'" class="d-flex flex-column align-center py-6">
        <v-progress-circular
          class="mb-4"
          color="primary"
          indeterminate
          size="64"
        />

        <p class="text-body-1 font-weight-medium">
          Envoi de la configuration...
        </p>
      </div>

      <div v-else-if="step === 'success'" class="d-flex flex-column align-center py-6">
        <v-icon
          class="mb-4"
          color="success"
          icon="mdi-check-circle-outline"
          size="80"
        />

        <h3 class="text-h6 font-weight-bold text-success">
          Configuration réussie !
        </h3>

        <p class="text-body-2 text-center mt-2">
          Le bouton redémarre et va se connecter au réseau Wi-Fi.
        </p>
      </div>
    </v-card-text>

    <v-card-actions v-if="step === 'init' || step === 'form'" class="px-4 pb-4">
      <v-spacer />

      <v-btn
        color="grey-darken-1"
        variant="text"
        @click="closeWizard"
      >
        Annuler
      </v-btn>

      <v-btn
        v-if="step === 'form'"
        color="primary"
        :disabled="!ssid"
        variant="flat"
        @click="sendCredentialsAndSave"
      >
        {{ isReconfigure ? 'Configurer' : 'Appairer' }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
/// <reference types="web-bluetooth" />

  import { computed, ref, shallowRef } from 'vue'
  import { paringTokenRepository } from '@/api/repository/paring-token.repository.ts'
  import { useAuthStore } from '@/stores/auth.ts'
  import { ToastType, useToastStore } from '@/stores/toast.store.ts'

  const props = defineProps({
    isReconfigure: {
      type: Boolean,
      default: false,
    },
  })

  const emit = defineEmits<{
    close: []
    success: []
  }>()

  const toastStore = useToastStore()
  const authStore = useAuthStore()

  // Check if current user is admin to show environment selector
  const isAdmin = computed(() => authStore.isAdmin)

  // --- STATE ---
  const ssid = ref('')
  const pass = ref('')
  const step = ref<'init' | 'scanning' | 'form' | 'pairing' | 'success'>('init')
  const availableNetworks = ref<string[]>([])

  // Default environment matching the C++ map keys
  const selectedEnv = ref<'prod' | 'prep' | 'dev'>('prod')

  // --- BLE PERSISTENCE ---
  // Using shallowRef to prevent Vue from wrapping native objects in deep proxies
  const bleServer = shallowRef<BluetoothRemoteGATTServer | null>(null)
  const bleService = shallowRef<BluetoothRemoteGATTService | null>(null)

  // UUIDs matching the ESP32 config
  const UUIDS = {
    service: '0e2867f8-3c99-43cf-96da-4085536c5025',
    ssid: '701a0149-77df-41c5-ad4c-5168a02a56af',
    pass: '157a59ea-184f-4473-8355-5e1ae5691a0d',
    token: 'f6d57167-1c28-4529-9354-7fdeaac4f1f0',
    cmd: 'e21a038c-6b98-4918-8b50-74c6d9fd01fe',
    env: 'bc399785-20a7-4343-b907-bdbe5044bcbd',
  }

  /**
   * Utility to pause execution for a given amount of time
   */
  function sleep (ms: number): Promise<void> {
    return new Promise(resolve => {
      setTimeout(resolve, ms)
    })
  }

  /**
   * Helper to write a string value to a specific BLE characteristic
   */
  async function writeChar (uuid: string, value: string): Promise<void> {
    if (!bleService.value) {
      throw new Error('Bluetooth disconnected')
    }

    const characteristic = await bleService.value.getCharacteristic(uuid)
    const encoder = new TextEncoder()

    await characteristic.writeValue(encoder.encode(value))
  }

  /**
   * Helper to read a string value from a specific BLE characteristic
   */
  async function readChar (uuid: string): Promise<string> {
    if (!bleService.value) {
      throw new Error('Bluetooth disconnected')
    }

    const characteristic = await bleService.value.getCharacteristic(uuid)
    const dataView = await characteristic.readValue()
    const decoder = new TextDecoder()

    return decoder.decode(dataView)
  }

  /**
   * Step 1: Connect to the device and request a Wi-Fi scan
   */
  async function connectAndScan (): Promise<void> {
    try {
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: [UUIDS.service] }],
      })

      if (!device.gatt) {
        throw new Error('GATT is not available on this device.')
      }

      step.value = 'scanning'

      // Connect and save instances globally for the next step
      bleServer.value = await device.gatt.connect()
      bleService.value = await bleServer.value.getPrimaryService(UUIDS.service)

      // Request ESP32 to start scanning
      await writeChar(UUIDS.cmd, 'SCAN')

      // Polling loop: check the characteristic until it's no longer "SCANNING"
      let scanResult = 'SCANNING'
      let attempts = 0

      while (scanResult === 'SCANNING' && attempts < 30) {
        await sleep(1000)
        scanResult = await readChar(UUIDS.cmd)
        attempts++
      }

      if (scanResult === 'SCANNING') {
        throw new Error('Timeout: The Wi-Fi scan took too long.')
      }

      // Parse the result string (SSIDs separated by newline)
      availableNetworks.value = scanResult && scanResult !== 'NONE' ? scanResult.split('\n') : []

      // Move to the form view
      step.value = 'form'
    } catch (error: any) {
      step.value = 'init'

      // Ignore user cancellation
      if (error?.name === 'NotFoundError' || error?.name === 'AbortError') {
        return
      }

      toastStore.notify(error.message || 'Bluetooth connection failed.', ToastType.Error)
    }
  }

  /**
   * Step 2: Send all collected data to ESP32 and trigger reboot
   */
  async function sendCredentialsAndSave (): Promise<void> {
    if (!ssid.value || !bleServer.value || !bleService.value) {
      return
    }

    step.value = 'pairing'

    try {
      let pairingToken = ''

      if (!props.isReconfigure) {
        pairingToken = await paringTokenRepository.getPairingToken()
      }

      // Send Wi-Fi credentials
      await writeChar(UUIDS.ssid, ssid.value)
      await writeChar(UUIDS.pass, pass.value)

      // Send Pairing Token if needed
      if (!props.isReconfigure) {
        await writeChar(UUIDS.token, pairingToken)
      }

      // send environment
      const targetEnv = isAdmin.value ? selectedEnv.value : 'prod'
      await writeChar(UUIDS.env, targetEnv)
      await sleep(200) // Give ESP32 flash time to settle

      // Trigger the SAVE command to validate and reboot the ESP32
      await writeChar(UUIDS.cmd, 'SAVE')
      await sleep(200)

      let response = ''
      try {
        response = await readChar(UUIDS.cmd)
      } catch {
      // Disconnect error is expected here because ESP32 reboots immediately on success
      }

      // Handle specific business errors returned before reboot
      if (response === 'ERR_ALREADY_REGISTERED') {
        throw new BleBusinessError('Le bouton est déjà associé à un autre utilisateur.')
      }

      if (response === 'ERR_SSID_EMPTY') {
        throw new BleBusinessError('Le nom du réseau Wi-Fi est vide.')
      }

      if (response === 'ERR_TOKEN_MISSING') {
        throw new BleBusinessError('Le token de connexion est manquant.')
      }

      // Success cleanup
      if (bleServer.value.connected) {
        bleServer.value.disconnect()
      }

      step.value = 'success'

      // Auto-close after a few seconds
      setTimeout(() => {
        emit('success')
        closeWizard()
      }, 3000)
    } catch (error: any) {
      step.value = 'form'

      if (error instanceof BleBusinessError) {
        toastStore.notify(error.message, ToastType.Error)
      } else {
        toastStore.notify('Erreur de configuration. Le bouton a peut-être été déconnecté.', ToastType.Error)
      }
    }
  }

  /**
   * Reset state and cleanly disconnect BLE
   */
  function closeWizard (): void {
    if (bleServer.value?.connected) {
      bleServer.value.disconnect()
    }

    step.value = 'init'
    ssid.value = ''
    pass.value = ''
    selectedEnv.value = 'prod'

    emit('close')
  }

  class BleBusinessError extends Error {
    constructor (message: string) {
      super(message)
      this.name = 'BleBusinessError'
    }
  }
</script>
